import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pin, PinOff, Loader2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * FestivalPollCard (iter153) — one-tap pinning of the "Where are we off
 * to next?" festival poll as the current active poll on the app.
 * Overrides the weekly rotation until Unpinned. Perfect for aligning
 * with a "vote in the poll" Reel campaign.
 */
export default function FestivalPollCard() {
  const [status, setStatus] = useState({ exists: false, pinned: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/polls/pin-festival/status");
      setStatus(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const pin = async () => {
    setBusy(true);
    try {
      await api.post("/admin/polls/pin-festival");
      toast.success("Festival poll pinned — it's now the active poll on the app.");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const unpin = async () => {
    setBusy(true);
    try {
      await api.post("/admin/polls/unpin-festival");
      toast.info("Festival poll unpinned — normal weekly rotation restored.");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-festival-poll"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            🎪 Festival poll
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            "Where are we off to next?"
          </h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Pin the festival poll (Boomtown · Beat-Herder · Bearded Theory · Camp Bestival · Green Man · Boardmasters) as the active poll on the app. Overrides the weekly rotation. Perfect to align with a "vote in the poll" Reel.
          </p>
        </div>
        <div
          className={`text-[11px] px-2 py-1 rounded-full font-semibold shrink-0 ${status.pinned ? "bg-[#ecfdf5] text-[#065f46]" : "bg-[#f5f5f4] text-[#666]"}`}
          data-testid="festival-poll-status-badge"
        >
          {status.pinned ? "📌 Currently pinned" : "Not pinned"}
        </div>
      </div>

      <div className="mt-4 flex gap-2 flex-wrap">
        <button
          type="button"
          disabled={busy || status.pinned}
          onClick={pin}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="festival-poll-pin-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pin className="w-4 h-4" />}
          {status.pinned ? "Already pinned" : "Pin as active poll"}
        </button>
        {status.pinned && (
          <button
            type="button"
            disabled={busy}
            onClick={unpin}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border hairline text-sm font-semibold hover:bg-[#f6f6f6] disabled:opacity-60"
            data-testid="festival-poll-unpin-btn"
          >
            <PinOff className="w-4 h-4" /> Unpin
          </button>
        )}
      </div>
    </section>
  );
}
